import React, { useState, useMemo } from 'react';
import { Card, Btn, Badge, Input, Select, EmptyState } from './UI';
import { Plus, Check, Trash2, RotateCcw, ChevronDown, ChevronUp, ListChecks } from 'lucide-react';
import { today, fmtDate } from '../utils/helpers';

// A personal, private to-do list shown ONLY on home. Each person (empId) sees only
// their own. Completing an item archives it (kept, not deleted). Fully independent
// from the Tasks tab. Writes go through the standard persist()/saveStore path.
const PRIORITY = {
  high:   { label: 'High',   color: 'red',    rank: 0 },
  medium: { label: 'Medium', color: 'orange', rank: 1 },
  low:    { label: 'Low',    color: 'gray',   rank: 2 },
};

function uid() {
  return 'td_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export default function TodoWidget({ store, persist, empId, title = 'My To-Dos' }) {
  const [text, setText]         = useState('');
  const [due, setDue]           = useState('');
  const [priority, setPriority] = useState('medium');
  const [showArchive, setShowArchive] = useState(false);

  const allTodos = store.todos || [];
  const mine = useMemo(() => allTodos.filter(t => t.empId === empId), [allTodos, empId]);

  const active = useMemo(() =>
    mine.filter(t => !t.done).sort((a, b) => {
      const pa = PRIORITY[a.priority]?.rank ?? 1, pb = PRIORITY[b.priority]?.rank ?? 1;
      if (pa !== pb) return pa - pb;
      // then by due date (soonest first; undated last)
      const da = a.dueDate || '9999-99-99', db = b.dueDate || '9999-99-99';
      return da < db ? -1 : da > db ? 1 : 0;
    }), [mine]);

  const archived = useMemo(() =>
    mine.filter(t => t.done).sort((a, b) => (b.doneAt || '').localeCompare(a.doneAt || '')), [mine]);

  function addTodo() {
    const t = text.trim();
    if (!t) return;
    const todo = {
      id: uid(), empId, title: t, dueDate: due || '', priority,
      done: false, doneAt: '', createdAt: today(), createdBy: empId,
    };
    persist({ ...store, todos: [...allTodos, todo] });
    setText(''); setDue(''); setPriority('medium');
  }

  function setDone(id, done) {
    persist({ ...store, todos: allTodos.map(t =>
      t.id === id ? { ...t, done, doneAt: done ? today() : '' } : t) });
  }

  function removeTodo(id) {
    persist({ ...store, todos: allTodos.filter(t => t.id !== id) }, { deletedIds: { todos: [id] } });
  }

  const isOverdue = t => t.dueDate && t.dueDate < today();

  const rowBase = {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '11px 14px', borderBottom: '1px solid var(--border)',
  };
  const checkBtn = (done) => ({
    width: 20, height: 20, minWidth: 20, borderRadius: 'var(--radius)', cursor: 'pointer',
    border: '2px solid ' + (done ? 'var(--green)' : 'var(--border2)'),
    background: done ? 'var(--green)' : 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
    transition: 'border-color .15s, background .15s',
  });

  return (
    <Card>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
        <span className="t-h3" style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
          <ListChecks size={18} style={{ color: 'var(--accent)' }} /> {title}
        </span>
        {active.length > 0 && (
          <span style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', fontSize: 12.5, color: 'var(--text3)', fontWeight: 600 }}>
            {active.length} to do
          </span>
        )}
      </div>

      {/* Add row */}
      <div style={{ display: 'flex', gap: 8, padding: '14px 16px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <Input
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Add a to-do…"
          style={{ flex: '2 1 200px', minWidth: 140 }}
        />
        <Input
          type="date"
          value={due}
          onChange={e => setDue(e.target.value)}
          style={{ flex: '1 1 140px', minWidth: 130 }}
        />
        <Select value={priority} onChange={e => setPriority(e.target.value)} style={{ flex: '0 1 120px', minWidth: 110 }}>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </Select>
        <Btn variant="primary" onClick={addTodo} disabled={!text.trim()}><Plus size={15} /> Add</Btn>
      </div>

      {/* Active list — scrolls inside the card */}
      <div style={{ maxHeight: 340, overflowY: 'auto' }}>
        {active.length === 0
          ? <div style={{ padding: '30px 20px' }}><EmptyState message="Nothing on your list — you're all clear." /></div>
          : active.map((t, i) => {
              const over = isOverdue(t);
              const pr = PRIORITY[t.priority] || PRIORITY.medium;
              return (
                <div key={t.id} style={{ ...rowBase, borderBottom: i < active.length - 1 ? rowBase.borderBottom : 'none' }}>
                  <button title="Mark done" onClick={() => setDone(t.id, true)} style={checkBtn(false)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500, wordBreak: 'break-word' }}>{t.title}</div>
                    {t.dueDate && (
                      <div style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', fontSize: 12, marginTop: 2, color: over ? 'var(--red)' : 'var(--text3)', fontWeight: over ? 600 : 500 }}>
                        {over ? 'Overdue · ' : 'Due '}{fmtDate(t.dueDate)}
                      </div>
                    )}
                  </div>
                  <Badge label={pr.label} color={pr.color} />
                  <button title="Delete" onClick={() => removeTodo(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 4, display: 'flex' }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--red)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; }}>
                    <Trash2 size={15} />
                  </button>
                </div>
              );
            })}
      </div>

      {/* Archive */}
      {archived.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          <button onClick={() => setShowArchive(v => !v)} style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 22px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 12.5, fontWeight: 600, color: 'var(--text3)', fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums',
          }}>
            <span>Archived · {archived.length} done</span>
            {showArchive ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showArchive && (
            <div style={{ maxHeight: 260, overflowY: 'auto', background: 'var(--bg2, transparent)' }}>
              {archived.map((t, i) => (
                <div key={t.id} style={{ ...rowBase, borderBottom: i < archived.length - 1 ? rowBase.borderBottom : 'none' }}>
                  <button title="Restore" onClick={() => setDone(t.id, false)} style={checkBtn(true)}>
                    <Check size={13} color="var(--bg)" />
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: 'var(--text3)', textDecoration: 'line-through', wordBreak: 'break-word' }}>{t.title}</div>
                    {t.doneAt && <div style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', fontSize: 11.5, marginTop: 2, color: 'var(--text3)' }}>Done {fmtDate(t.doneAt)}</div>}
                  </div>
                  <button title="Restore" onClick={() => setDone(t.id, false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 4, display: 'flex' }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; }}>
                    <RotateCcw size={14} />
                  </button>
                  <button title="Delete" onClick={() => removeTodo(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 4, display: 'flex' }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--red)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
